import { useState } from 'react';
import { FileVideo, Check, ChevronDown, ChevronUp, Loader2, AlertTriangle, ClipboardList, Palette, Sparkles, Package, Video, ArrowRight, Monitor, Hand, MousePointer2, Film, Map } from 'lucide-react';

/**
 * VideoConfirmation 数据结构（与服务端一致）
 */
interface VideoSegment {
  index: number;
  fileName: string;
  duration: number;
  connectionMode: string;   // freeze / loop / auto_jump / auto_jump_endcard
  gesture?: string;         // press / swipe_up / swipe_down / ...
  clickArea?: string;       // fullscreen / center / custom
}

interface EndCardInfo {
  present: boolean;
  trigger: string;          // fullscreen_click / button_click / swipe / auto
  triggerEvent: string;     // open_store
  hasImage?: boolean;       // 是否已上传结束页背景图
}

interface VideoConfirmation {
  videoCount: number;
  videos: VideoSegment[];
  endCard?: EndCardInfo;
  particles: string[];
  audioEffects: string[];
  defaults: string[];
  summary: string;
}

interface VideoConfirmCardProps {
  confirmation: VideoConfirmation;
  onConfirm?: () => void;
  loading?: boolean;
  confirmed?: boolean;
}

/** 连接方式中文映射 */
const MODE_LABEL: Record<string, string> = {
  freeze: '互动定格',
  loop: '互动循环',
  auto_jump: '自动跳转',
  auto_jump_endcard: '自动跳转结束页',
};

/** 手势中文映射 */
const GESTURE_LABEL: Record<string, string> = {
  press: '按下',
  swipe_up: '上滑',
  swipe_down: '下滑',
  swipe_left: '左滑',
  swipe_right: '右滑',
  swipe_any: '任意滑动',
};

/** 点击区域中文映射 */
const AREA_LABEL: Record<string, string> = {
  fullscreen: '全屏',
  center: '居中',
};

/** 触发方式中文映射 */
const TRIGGER_LABEL: Record<string, string> = {
  fullscreen_click: '全屏点击',
  button_click: '按钮点击',
  swipe: '滑动',
  auto: '自动',
};

/** 粒子效果中文映射（与 video-playable runtime 支持的 effect 对齐） */
const PARTICLE_LABEL: Record<string, string> = {
  confetti: '彩纸屑',
  money: '金币雨',
  coins: '金币',
  redpacket: '红包雨',
  fireworks: '烟花',
  hearts: '爱心',
  generic: '默认粒子',
};

/** 音效中文映射（与 SfxPlayer 支持的 name 对齐） */
const AUDIO_LABEL: Record<string, string> = {
  click: '点击音效',
  swipe: '滑动音效',
  success: '成功音效',
  jump: '跳转音效',
  countdown: '倒计时音效',
  mute_original_audio: '静音原声',
  add_background_music: '背景音乐',
  add_interaction_sfx: '交互音效',
};

/** 连接方式样式映射 */
const MODE_STYLE: Record<string, string> = {
  freeze: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
  loop: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
  auto_jump: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
  auto_jump_endcard: 'bg-purple-500/10 border-purple-500/30 text-purple-400',
};

/**
 * 视频转试玩动作流：6 阶段流水线可视化（与左侧 Pipeline 组件统一）
 * 视频分析 → 互动方案 → 素材生成 → 视频转码 → 试玩装配 → 打包发布
 */
function ActionFlow(): JSX.Element {
  const stages = [
    { key: 'video-start', label: '视频分析', icon: ClipboardList, color: 'text-neon-cyan' },
    { key: 'video-confirmation', label: '互动方案', icon: MousePointer2, color: 'text-neon-purple' },
    { key: 'video-frame-analyze', label: '画面定位', icon: Map, color: 'text-neon-orange' },
    { key: 'asset_prepare', label: '素材生成', icon: Palette, color: 'text-neon-purple' },
    { key: 'video-transcode', label: '视频转码', icon: Film, color: 'text-neon-cyan' },
    { key: 'video-assemble', label: '试玩装配', icon: Sparkles, color: 'text-neon-pink' },
    { key: 'publish', label: '打包发布', icon: Package, color: 'text-neon-green' },
  ];

  return (
    <div className="mb-3 flex items-center justify-between rounded-lg bg-muted/30 px-3 py-2">
      {stages.map((s, i) => (
        <div key={s.key} className="flex items-center gap-1.5">
          {i > 0 && <ArrowRight size={12} className="text-muted-foreground/40 shrink-0" />}
          <div className={`flex items-center gap-1 rounded-full border border-border/40 bg-background/60 px-2 py-0.5`}>
            <s.icon size={10} className={`${s.color}`} />
            <span className="text-[10px] text-foreground/70 whitespace-nowrap">{s.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * 视频转试玩确认卡片
 *
 * 展示 AI 对视频需求的理解摘要，含动作流可视化、视频段配置、结束页信息、兜底规则，
 * 提供「确认生成」和「调整需求」两个操作按钮。
 */
export function VideoConfirmCard({
  confirmation,
  onConfirm,
  loading = false,
  confirmed = false,
}: VideoConfirmCardProps): JSX.Element {
  const [showDefaults, setShowDefaults] = useState(false);

  const hasEndCard = confirmation.endCard?.present;

  return (
    <div className="glass-card gradient-border max-w-[90%] overflow-hidden">
      {/* 顶部标题条 */}
      <div className="flex items-center gap-2 border-b border-border/40 bg-gradient-to-r from-neon-cyan/[0.08] via-transparent to-neon-purple/[0.08] px-4 py-2.5">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-neon-cyan/30 bg-neon-cyan/10 shadow-[0_0_12px_hsl(var(--neon-cyan)/0.25)]">
          <FileVideo size={12} className="text-neon-cyan" />
        </span>
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-neon-cyan">
          视频转试玩确认
        </span>
        <span className="ml-auto flex items-center gap-1 rounded-full border border-neon-cyan/30 bg-neon-cyan/10 px-2 py-0.5 text-[10px] font-medium text-neon-cyan">
          <Video size={9} />
          {confirmation.videoCount} 个视频
        </span>
      </div>

      {/* 动作流可视化 */}
      <div className="px-4 pt-3">
        <ActionFlow />
      </div>

      {/* AI 理解摘要 */}
      <div className="px-4 pb-2">
        <p className="text-xs leading-relaxed text-foreground/80">{confirmation.summary}</p>
      </div>

      {/* 视频段表格 */}
      <div className="px-4 pb-2">
        <div className="mb-1.5 flex items-center gap-1.5">
          <Monitor size={11} className="text-neon-cyan/60" />
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">视频片段</span>
        </div>
        <div className="overflow-hidden rounded-lg border border-border/40">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-border/30 bg-muted/40">
                <th className="px-2 py-1.5 text-left font-medium text-muted-foreground">序号</th>
                <th className="px-2 py-1.5 text-left font-medium text-muted-foreground">文件</th>
                <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">时长</th>
                <th className="px-2 py-1.5 text-left font-medium text-muted-foreground">连接方式</th>
                <th className="px-2 py-1.5 text-left font-medium text-muted-foreground">手势</th>
                <th className="px-2 py-1.5 text-left font-medium text-muted-foreground">区域</th>
              </tr>
            </thead>
            <tbody>
              {confirmation.videos.map((v, idx) => (
                <tr key={v.index} className={idx > 0 ? 'border-t border-border/20' : ''}>
                  <td className="px-2 py-1.5 text-foreground/70">#{v.index}</td>
                  <td className="max-w-[120px] truncate px-2 py-1.5 text-foreground/90" title={v.fileName}>
                    {v.fileName}
                  </td>
                  <td className="px-2 py-1.5 text-right text-foreground/70">{v.duration.toFixed(1)}s</td>
                  <td className="px-2 py-1.5">
                    <span className={`inline-block rounded-full border px-1.5 py-px text-[10px] ${MODE_STYLE[v.connectionMode] || 'bg-muted/40 text-muted-foreground'}`}>
                      {MODE_LABEL[v.connectionMode] || v.connectionMode}
                    </span>
                  </td>
                  <td className="px-2 py-1.5">
                    {v.gesture ? (
                      <span className="inline-flex items-center gap-0.5 text-foreground/70">
                        <Hand size={9} />
                        {GESTURE_LABEL[v.gesture] || v.gesture}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/50">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    {v.clickArea ? (
                      <span className="inline-flex items-center gap-0.5 text-foreground/70">
                        <MousePointer2 size={9} />
                        {AREA_LABEL[v.clickArea] || v.clickArea}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/50">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 结束页信息 */}
      <div className="px-4 pb-2">
        <div className="flex items-center gap-2 rounded-lg border border-border/30 bg-muted/20 px-3 py-2">
          <span className={`flex h-5 w-5 items-center justify-center rounded-full ${hasEndCard ? 'bg-emerald-500/15' : 'bg-muted/40'}`}>
            <Check size={9} className={hasEndCard ? 'text-emerald-400' : 'text-muted-foreground'} />
          </span>
          <span className="text-xs text-foreground/80">
            {hasEndCard ? (
              <>结束页：<span className="font-medium text-foreground/90">有</span>
                <span className="mx-1 text-muted-foreground">·</span>
                触发：{TRIGGER_LABEL[confirmation.endCard!.trigger] || confirmation.endCard!.trigger}
                <span className="mx-1 text-muted-foreground">·</span>
                事件：{confirmation.endCard!.triggerEvent === 'open_store' ? '应用商店安装' : confirmation.endCard!.triggerEvent}
                <span className="mx-1 text-muted-foreground">·</span>
                背景：{confirmation.endCard!.hasImage ? '已上传图片' : '纯白兜底页'}
              </>
            ) : (
              <>结束页：<span className="font-medium text-muted-foreground">无</span></>
            )}
          </span>
        </div>
        {hasEndCard && !confirmation.endCard!.hasImage && (
          <div className="mt-1.5 rounded-lg border border-dashed border-border/40 bg-muted/20 px-3 py-1.5">
            <p className="text-[10px] text-muted-foreground">
              未上传结束页背景图，将使用纯白兜底页。如需定制，可补充标题、icon 图或 CTA 按钮文案。
            </p>
          </div>
        )}
      </div>

      {/* 粒子/音效信息 */}
      <div className="flex flex-wrap gap-2 px-4 pb-2">
        {(confirmation.particles?.length ?? 0) > 0 ? (
          <span className="rounded-full border border-neon-pink/20 bg-neon-pink/5 px-2 py-0.5 text-[10px] text-neon-pink">
            🎆 粒子：{confirmation.particles.map((p) => PARTICLE_LABEL[p] ?? p).join('、')}
          </span>
        ) : (
          <span className="rounded-full border border-neon-pink/10 bg-neon-pink/5 px-2 py-0.5 text-[10px] text-neon-pink/70">
            🎆 粒子：未指定
          </span>
        )}
        {(confirmation.audioEffects?.length ?? 0) > 0 ? (
          <span className="rounded-full border border-neon-blue/20 bg-neon-blue/5 px-2 py-0.5 text-[10px] text-neon-blue">
            🔊 音效：{confirmation.audioEffects.map((a) => AUDIO_LABEL[a] ?? a).join('、')}
          </span>
        ) : (
          <span className="rounded-full border border-neon-blue/10 bg-neon-blue/5 px-2 py-0.5 text-[10px] text-neon-blue/70">
            🔊 音效：未指定
          </span>
        )}
      </div>

      {/* 兜底规则（折叠） */}
      {confirmation.defaults?.length > 0 && (
        <div className="border-t border-border/30 px-4 py-2">
          <button
            type="button"
            onClick={() => setShowDefaults(!showDefaults)}
            className="flex w-full items-center gap-1 text-[10px] text-muted-foreground transition-colors hover:text-foreground/80"
          >
            <AlertTriangle size={10} />
            <span>AI 兜底规则（{confirmation.defaults.length} 项）</span>
            {showDefaults ? <ChevronUp size={11} className="ml-auto" /> : <ChevronDown size={11} className="ml-auto" />}
          </button>
          {showDefaults && (
            <ul className="mt-1.5 space-y-0.5 pl-4">
              {confirmation.defaults.map((d, i) => (
                <li key={i} className="list-disc text-[10px] leading-relaxed text-muted-foreground/80">{d}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* 底部操作按钮 */}
      <div className="flex flex-col border-t border-border/40 px-4 py-2.5">
        {confirmed ? (
          <div className="flex items-center justify-end gap-1.5 text-xs font-medium text-neon-green">
            <Check size={13} />
            已确认，进入生成流程
          </div>
        ) : <div className="flex justify-end">
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="glow-btn flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs font-semibold text-white transition-all disabled:pointer-events-none disabled:opacity-50"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            {loading ? '正在生成…' : '确认生成'}
          </button>
        </div>}
        <p className="mt-1.5 text-center text-[11px] text-muted-foreground/60">
          💡 如需调整，直接在下方对话框输入修改意见即可
        </p>
      </div>
    </div>
  );
}
